// Interface language (English / Indonesian). Texts are written in English in the code and wrapped in t();
// the Indonesian dictionaries in ./i18n/id/*.ts map each English text to its translation. A missing entry
// falls back to English, so an untranslated text is never blank. Messages produced by the server (job logs,
// archive analysis reasons) stay in English.
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

export type Lang = "en" | "id";
const KEY = "adr-lang";

const ID: Record<string, string> = Object.assign(
  {},
  ...Object.values(import.meta.glob<{ default: Record<string, string> }>("./i18n/id/*.ts", { eager: true })).map((m) => m.default),
);

function readLang(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "en" || v === "id") return v;
  } catch {
    /* storage unavailable */
  }
  return navigator.language?.toLowerCase().startsWith("id") ? "id" : "en";
}

let current: Lang = readLang();
document.documentElement.lang = current;

export function getLang(): Lang {
  return current;
}

/** Locale for number/date formatting. */
export function locale(): string | undefined {
  return current === "id" ? "id-ID" : undefined;
}

/** Translate an English UI text. ``{name}`` placeholders are replaced from ``vars``. */
export function t(text: string, vars?: Record<string, string | number | null | undefined>): string {
  let out = current === "id" ? ID[text] ?? text : text;
  if (import.meta.env.DEV && current === "id" && !(text in ID)) console.debug("[i18n] missing:", text);
  if (vars) out = out.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k] ?? "") : m));
  return out;
}

/** Singular/plural English helper; each form is translated separately. */
export function tn(n: number, one: string, many: string, vars?: Record<string, string | number | null | undefined>): string {
  return t(n === 1 ? one : many, { n: n.toLocaleString(locale()), ...vars });
}

const Ctx = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: current, setLang: () => undefined });

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setState] = useState<Lang>(current);
  const setLang = useCallback((l: Lang) => {
    current = l;
    document.documentElement.lang = l;
    try {
      localStorage.setItem(KEY, l);
    } catch {
      /* ignore */
    }
    setState(l);
  }, []);
  // Re-mounting the tree on a language change re-renders every t() call; data stays in the query cache.
  return <Ctx.Provider value={{ lang, setLang }}><div key={lang} className="contents">{children}</div></Ctx.Provider>;
}

export function useLang() {
  return useContext(Ctx);
}
